import { camelToTitle } from '@shared/utils';
import { toOperationFamily } from '@/lib/activityOperations/activityOperationHelpers.js';

const OUTCOME_BY_FAMILY = { create: 'created', update: 'updated', delete: 'deleted' };

// buildPieveOperationSearchKeys puts the action's operation and model at the head of every row's search keys so
// Governance can search by them; a change already says both (its group, its title), so they are not record fields.
const SEARCH_KEY_ACTION_FIELDS = new Set(['operationType', 'model']);

// Failures lead: they are the one group someone has to act on. The rest follow the order every count uses.
const OUTCOME_GROUPS = [
  { outcome: 'failed', label: 'Failed — needs attention' },
  { outcome: 'created', label: 'Created' },
  { outcome: 'updated', label: 'Updated' },
  { outcome: 'deleted', label: 'Deleted' }
];

/* A run's governance ledger rows, read the way a person checks a run: what changed in their systems, grouped by
   outcome with failures first, and apart from that the workflow's own bookkeeping.

   The counts follow the API's change counts exactly (listOperationCounts): a failed write is counted once, as failed,
   never as the change it attempted; and a write to an internal app (`category` other than `app`, today Key-Value
   Storage) is bookkeeping, listed but not counted. An app missing from the catalog counts as a business system, as it
   does on the server. Upserts and generic writes are updates, as in Governance.

   Each change points at the time machine row of its call (`rowIndex`): exactly, by the call's `stepInstanceId`, when
   the ledger row carries one; otherwise only at its step's first execution (`isExactCall` false), which for a step
   inside a loop may be a different iteration — so only an exact call shows what it sent and got back. */
const buildWorkflowRunChanges = ({ operations = [], appsMetadataItems = [], rows = [] } = {}) => {
  const appBySlug = new Map(appsMetadataItems.map((app) => [app.slug, app]));
  const rowIndexByStepInstanceId = new Map(rows.map((row) => [row.stepInstanceId, row.index]));
  const firstRowIndexByStepId = new Map();

  for (const row of rows) {
    if (!firstRowIndexByStepId.has(row.stepId)) {
      firstRowIndexByStepId.set(row.stepId, row.index);
    }
  }

  const changes = [...operations]
    .sort((left, right) => (left.batchIndex ?? 0) - (right.batchIndex ?? 0))
    .map((operation) => {
      const app = appBySlug.get(operation.appSlug);
      const exactRowIndex = operation.stepInstanceId ? rowIndexByStepInstanceId.get(operation.stepInstanceId) : undefined;

      return {
        operationId: operation.operationId,
        outcome: operation.status === 'error' ? 'failed' : OUTCOME_BY_FAMILY[toOperationFamily(operation.operationType)],
        isInternal: Boolean(app) && app.category !== 'app',
        appName: app?.name || operation.appName || operation.appSlug,
        appIcon: app?.icon || null,
        title: operation.summary || operation.actionName || operation.actionSlug,
        entityId: operation.entityId,
        // The record's identifying fields the ledger kept (ids, names, numbers, emails — never secrets).
        identifiers: (operation.searchKeys || []).filter((searchKey) => !SEARCH_KEY_ACTION_FIELDS.has(searchKey.key)).map((searchKey) => ({ label: camelToTitle(searchKey.key), value: searchKey.value })),
        stepId: operation.stepId,
        errorMessage: operation.errorMessage,
        // Which pass of a loop made it, so changes from one looped step read apart (only an exact call knows).
        iteration: exactRowIndex !== undefined && rows[exactRowIndex].loopIndex !== null ? rows[exactRowIndex].loopIndex + 1 : null,
        rowIndex: exactRowIndex ?? firstRowIndexByStepId.get(operation.stepId) ?? null,
        isExactCall: exactRowIndex !== undefined
      };
    });
  const externalChanges = changes.filter((change) => !change.isInternal);
  const countOf = (outcome) => externalChanges.filter((change) => change.outcome === outcome).length;

  return {
    counts: { created: countOf('created'), updated: countOf('updated'), deleted: countOf('deleted'), failed: countOf('failed') },
    groups: OUTCOME_GROUPS.map((group) => ({ ...group, changes: externalChanges.filter((change) => change.outcome === group.outcome) })).filter((group) => group.changes.length > 0),
    internalChanges: changes.filter((change) => change.isInternal),
    recordedCount: operations.length
  };
};

export default buildWorkflowRunChanges;
